// src/components/functions/LogicBlockConfig.js
import { isEditLeaseLost } from '../../utils/functionMutation';
import React, { useState, useEffect, useRef, useMemo, forwardRef, useImperativeHandle } from 'react';
import {
  Box,
  Typography,
  Button,
  TextField,
  Stack,
  Divider,
  Grid,
  Chip,
  Tab,
  Tabs,
  Alert
} from '@mui/material';
import CopyButton from '../common/CopyButton';
import NodeOutputsPreview from './NodeOutputsPreview';
import { doc, collection, query, orderBy, onSnapshot } from 'firebase/firestore';
import { mainAuth, db } from '../../services/firebase';
import { formatText, formatTextForDatabase } from '../common/TextFormatterProvider';
import { functionEndpoint } from '../../services/endpoints';
import MessageDisplay from '../assistant/MessageDisplay';
import MessageInput from '../assistant/MessageInput';
import HelpButton from '../common/HelpButton';
import { PYTHON_TOKEN_COLOURS, tokenisePython } from '../../utils/pythonHighlight';
import { v4 as uuidv4 } from 'uuid';
import { toolState } from '../../utils/logicToolState';

const inferUploadKind = (filename = '') => {
  const ext = filename.split('.').pop()?.toLowerCase();
  if (['xlsx', 'xls', 'csv', 'json', 'docx'].includes(ext)) return 'structured';
  if (['pdf'].includes(ext)) return 'pdf';
  if (['png', 'jpg', 'jpeg', 'gif', 'webp'].includes(ext)) return 'image';
  return 'file';
};

const CODE_TEXT_SX = {
  m: 0,
  py: 1.25,
  flexShrink: 0,
  fontFamily: 'SFMono-Regular, Menlo, Consolas, "Liberation Mono", monospace',
  fontSize: '12.5px',
  lineHeight: 1.6,
  whiteSpace: 'pre',
};

// The committed `last_code_snippet` from the block document — the exact code the
// runtime executes for this node. Read-only: code is only ever written by the
// Logic Assistant's implement step.
const CodePanel = ({ code }) => {
  const tokens = useMemo(() => tokenisePython(code), [code]);

  if (!code) {
    return (
      <Typography variant="body2" sx={{ p: 2, color: '#ADB5BD' }}>
        This node has no committed code yet. Ask the assistant to implement the node's logic.
      </Typography>
    );
  }

  const lineNumbers = code.split('\n').map((_, index) => index + 1).join('\n');

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1, flexShrink: 0 }}>
        <Typography variant="caption" sx={{ color: '#ADB5BD' }}>
          Committed code — runs unchanged on every execution of this node.
        </Typography>
        <CopyButton text={code} title="Copy code" sx={{ color: '#ADB5BD' }} />
      </Box>
      <Box sx={{
        flexGrow: 1,
        minHeight: 0,
        overflow: 'auto',
        border: '1px solid rgba(255, 255, 255, 0.08)',
        borderRadius: 1,
        backgroundColor: '#12161D',
      }}>
        <Box sx={{ display: 'flex', minWidth: 'min-content' }}>
          <Box component="pre" sx={{
            ...CODE_TEXT_SX,
            px: 1.25,
            position: 'sticky',
            left: 0,
            textAlign: 'right',
            userSelect: 'none',
            color: '#5A6472',
            backgroundColor: '#12161D',
            borderRight: '1px solid rgba(255, 255, 255, 0.08)',
          }}>
            {lineNumbers}
          </Box>
          <Box component="pre" sx={{ ...CODE_TEXT_SX, px: 1.5, color: '#F0F2F5' }}>
            {tokens.map((token, index) => (
              <Box
                component="span"
                key={index}
                sx={{
                  color: PYTHON_TOKEN_COLOURS[token.kind],
                  fontStyle: token.kind === 'comment' ? 'italic' : 'normal',
                }}
              >
                {token.text}
              </Box>
            ))}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

// The stored logic_summary is plain text, one numbered step per line
// ("1. ...", "2. ..."). Parse it so the display owns the presentation.
// Unnumbered leading lines become a preamble; unnumbered lines after a
// step are treated as that step's continuation.
const parseLogicSummary = (summary) => {
  const steps = [];
  const preamble = [];
  summary.split('\n').forEach((rawLine) => {
    const line = rawLine.trim();
    if (!line) return;
    const numbered = line.match(/^(\d+)[.)]\s+(.*)$/);
    if (numbered) {
      steps.push(numbered[2]);
    } else if (steps.length === 0) {
      preamble.push(line);
    } else {
      steps[steps.length - 1] += `\n${line}`;
    }
  });
  return { preamble: preamble.join(' '), steps };
};

const LogicSummaryPanel = ({ summary }) => {
  const { preamble, steps } = useMemo(() => parseLogicSummary(summary), [summary]);

  // A summary with no numbered lines renders as-is rather than disappearing.
  if (steps.length === 0) {
    return (
      <Typography variant="body2" sx={{ p: 2, whiteSpace: 'pre-wrap' }}>{summary}</Typography>
    );
  }

  return (
    <Box sx={{ border: '1px solid rgba(255, 255, 255, 0.08)', borderRadius: 1, p: 2, backgroundColor: 'rgba(126, 88, 181, 0.05)' }}>
      {preamble && (
        <Typography variant="body2" sx={{ mb: 2, color: '#ADB5BD' }}>{preamble}</Typography>
      )}
      {steps.map((step, index) => (
        <Box key={index} sx={{ display: 'flex', gap: 1.5 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <Box sx={{
              width: 24,
              height: 24,
              borderRadius: '50%',
              flexShrink: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: 'rgba(126, 88, 181, 0.25)',
              color: '#D4B8FA',
              fontSize: '12px',
              fontWeight: 600,
            }}>
              {index + 1}
            </Box>
            {index < steps.length - 1 && (
              <Box sx={{ width: '1px', flexGrow: 1, my: 0.5, backgroundColor: 'rgba(126, 88, 181, 0.35)' }} />
            )}
          </Box>
          <Typography variant="body2" sx={{
            pt: 0.375,
            pb: index < steps.length - 1 ? 2 : 0,
            whiteSpace: 'pre-wrap',
            lineHeight: 1.6,
          }}>
            {step}
          </Typography>
        </Box>
      ))}
    </Box>
  );
};

const NodeOutputsPanel = ({
  manifest,
  outputGcsPath,
  livePreviewData,
  code,
  logicSummary,
  activeTab,
  onTabChange,
}) => {
  const showingLogic = activeTab === 1;
  const showingCode = activeTab === 2;

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Tabs
        value={activeTab}
        onChange={(_, value) => onTabChange(value)}
        sx={{
          mb: 1,
          flexShrink: 0,
          minHeight: 36,
          borderBottom: '1px solid rgba(255, 255, 255, 0.08)',
          '& .MuiTab-root': { minHeight: 36, py: 0.5, color: '#ADB5BD' },
          '& .Mui-selected': { color: '#7E58B5 !important' },
          '& .MuiTabs-indicator': { backgroundColor: '#7E58B5' },
        }}
      >
        <Tab label="Node Outputs" />
        <Tab label="Logic" />
        <Tab label="Code" />
      </Tabs>
      {/* Every pane stays mounted so switching tabs never refetches the preview
          data or the chart specs from GCS. */}
      <Box sx={{ flexGrow: 1, minHeight: 0 }}>
        <Box sx={{ height: '100%', overflow: 'auto', pr: 0.5, display: activeTab === 0 ? 'block' : 'none' }}>
          <NodeOutputsPreview
            manifest={manifest}
            outputGcsPath={outputGcsPath}
            livePreviewData={livePreviewData}
          />
        </Box>

        <Box sx={{ height: '100%', overflow: 'auto', pr: 0.5, display: showingLogic ? 'block' : 'none' }}>
          {logicSummary ? (
            <LogicSummaryPanel summary={logicSummary} />
          ) : (
            <Typography variant="body2" sx={{ p: 2, color: '#ADB5BD' }}>
              Logic Summary is missing. It will be populated next time you interact with the logic assistant.
            </Typography>
          )}
        </Box>

        <Box sx={{ height: '100%', display: showingCode ? 'block' : 'none' }}>
          <CodePanel code={code} />
        </Box>
      </Box>
    </Box>
  );
};

const LogicBlockConfig = forwardRef(function LogicBlockConfig({
  initialConfig = {},
  onSave,
  onCancel,
  companyName,
  functionId,
  blockId,
  // The builder session's lease identity: sent with every assistant request
  // so the backend can refuse writes from a displaced session (H52).
  sessionId,
  // Lets the builder abort an in-flight assistant stream on lease loss.
  registerAssistantAbort,
  onEditLeaseLost,
  onImplementationCommitted,
  // Draft prompt text owned by the builder so it survives this component
  // unmounting (accidental Save/Cancel) until saved, the node is deleted,
  // or the builder closes.
  draftMessage = '',
  onDraftMessageChange,
  draftFiles = [],
  draftSubmissionId = null,
  onDraftFilesChange,
  onDraftSubmitted,
  onDraftSaved,
}, ref) {
  const [name, setName] = useState(formatText(initialConfig.name || ''));
  const [messages, setMessages] = useState([]);
  const [streamingMessage, setStreamingMessage] = useState(null);
  const [pendingUserMessage, setPendingUserMessage] = useState(null);
  const [outputGcsPath, setOutputGcsPath] = useState(null);
  const [livePreviewData, setLivePreviewData] = useState(null);
  const [artifactsManifest, setArtifactsManifest] = useState(initialConfig.artifacts_manifest || []);
  const [manifestGeneration, setManifestGeneration] = useState(initialConfig.manifest_generation || null);
  const [lastCodeSnippet, setLastCodeSnippet] = useState(initialConfig.last_code_snippet || '');
  const [logicSummary, setLogicSummary] = useState(initialConfig.logic_summary || '');
  const [repairReason, setRepairReason] = useState('');
  // Held here rather than inside the panel so a new manifest generation
  // remounts the output renderers without resetting the tab. A generation
  // produced by this session's stream is adopted when its outputs first render
  // live, so the commit snapshot that follows (same generation) does not
  // remount them a second time.
  const [activePanelTab, setActivePanelTab] = useState(0);
  const [error, setError] = useState('');
  const initialLoadRef = useRef(true);
  const streamingCleanupTimerRef = useRef(null);
  const liveImplementGenerationRef = useRef(null);
  const requestRef = useRef(null);
  const committedBlockRef = useRef(initialConfig);

  const restoreCommittedOutputs = () => {
    const block = committedBlockRef.current;
    setArtifactsManifest(block.artifacts_manifest || []);
    setManifestGeneration(block.manifest_generation || null);
    setOutputGcsPath(block.output_data_gcs_path || null);
    setLivePreviewData(null);
    liveImplementGenerationRef.current = null;
  };

  useEffect(() => {
    if (!companyName || !functionId || !blockId) return;

    const blockDocRef = doc(db, `users/${companyName}/data_functions/${functionId}/blocks`, blockId);
    
    // Listen to the block document for its config (like the final GCS path)
    const unsubBlock = onSnapshot(blockDocRef, (docSnap) => {
        if (docSnap.exists()) {
            const blockData = docSnap.data();
            committedBlockRef.current = blockData;
            setRepairReason(blockData.logic_revalidation_required ? blockData.logic_revalidation_reason : '');
            // Only set name on initial load to avoid clearing user's unsaved changes
            if (initialLoadRef.current) {
                setName(formatText(blockData.name || ''));
                initialLoadRef.current = false;
                setLoadedGeneration((n) => n + 1);
            }
            setOutputGcsPath(blockData.output_data_gcs_path || null);
            setLastCodeSnippet(blockData.last_code_snippet || '');
            setLogicSummary(blockData.logic_summary || '');
            if (
              !liveImplementGenerationRef.current
              || blockData.manifest_generation === liveImplementGenerationRef.current
              || !blockData.assistant_run_lease
            ) {
              setArtifactsManifest(blockData.artifacts_manifest || []);
              setManifestGeneration(blockData.manifest_generation || null);
              setLivePreviewData(null);
              liveImplementGenerationRef.current = null;
            }
        }
    });

    // Listen to the messages subcollection
    const messagesCollectionRef = collection(blockDocRef, 'messages');
    const q = query(messagesCollectionRef, orderBy('timestamp'));
    
    const unsubMessages = onSnapshot(q, (querySnapshot) => {
      const fetchedMessages = querySnapshot.docs
          .map(doc => ({...doc.data(), id: doc.id}))
          .filter(msg => msg.type !== 'compaction');
      setMessages(fetchedMessages);
    }, (err) => {
        console.error("Error fetching messages:", err);
        setError("Failed to load message history.");
    });

    return () => {
      unsubBlock();
      unsubMessages();
    };
  }, [companyName, functionId, blockId]);

  useEffect(() => {
    if (pendingUserMessage && messages.some(msg =>
      msg.role === 'user' && msg.id === pendingUserMessage.id
    )) {
      setPendingUserMessage(null);
    }
  }, [messages, pendingUserMessage]);

  useEffect(() => {
    if (draftSubmissionId && messages.some(message => (
      message.role === 'user' && message.id === draftSubmissionId
    ))) {
      onDraftSaved(draftSubmissionId);
    }
  }, [messages, draftSubmissionId, onDraftSaved]);

  useEffect(() => {
    if (streamingCleanupTimerRef.current) {
      clearTimeout(streamingCleanupTimerRef.current);
      streamingCleanupTimerRef.current = null;
    }

    if (streamingMessage && messages.some(
      msg => msg.role === 'assistant' && msg.id === streamingMessage.id
    )) {
      streamingCleanupTimerRef.current = setTimeout(() => {
        setStreamingMessage(null);
      }, 100);
    }

    return () => {
      if (streamingCleanupTimerRef.current) {
        clearTimeout(streamingCleanupTimerRef.current);
        streamingCleanupTimerRef.current = null;
      }
    };
  }, [messages, streamingMessage]);

  const handleSendMessage = async (userMessage, files = []) => {
    if ((!userMessage.trim() && files.length === 0) || requestRef.current) return;
    setError('');
    setLivePreviewData(null); // Clear previous live data
    liveImplementGenerationRef.current = null;
    // An unchanged unacknowledged submission keeps its ID on retry. The
    // backend will refuse to save that same user message a second time.
    const userMessageId = draftSubmissionId || uuidv4();
    requestRef.current = userMessageId;
    onDraftSubmitted(userMessageId);

    // Add pending user message immediately (include file indicators)
    const pendingContent = userMessage.trim() ? [{ type: 'text', text: userMessage }] : [];
    files.forEach((file) => {
      const kind = inferUploadKind(file.name);
      pendingContent.push({
        type: kind === 'image' || kind === 'pdf' ? 'images' : 'file_upload',
        filename: file.name,
        kind,
      });
    });
    setPendingUserMessage({
        id: userMessageId,
        role: 'user',
        content: pendingContent,
        timestamp: new Date()
    });

    setStreamingMessage({
        id: `streaming-${Date.now()}`, role: 'assistant', content: [], streaming: true
    });

    let sawStreamError = false;
    let sawDone = false;
    const abortController = new AbortController();
    if (registerAssistantAbort) registerAssistantAbort(abortController);
    try {
      const emulatedOrg = sessionStorage.getItem('emulatedOrganisation');
      const idToken = await mainAuth.currentUser.getIdToken();
      const url = functionEndpoint('logic_assistant');
      let response;

      if (files.length > 0) {
        const formData = new FormData();
        formData.append('function_id', functionId);
        formData.append('block_id', blockId);
        formData.append('session_id', sessionId || '');
        formData.append('user_message_id', userMessageId);
        formData.append('user_message', userMessage.trim());
        files.forEach((file) => formData.append('files', file));

        const headers = { 'Authorization': `Bearer ${idToken}` };
        if (emulatedOrg) headers['X-Emulated-Organisation'] = emulatedOrg;

        response = await fetch(url, {
          method: 'POST',
          headers,
          body: formData,
          signal: abortController.signal,
        });
      } else {
        const headers = { 'Content-Type': 'application/json', 'Authorization': `Bearer ${idToken}` };
        if (emulatedOrg) headers['X-Emulated-Organisation'] = emulatedOrg;

        response = await fetch(url, {
          method: 'POST', headers,
          body: JSON.stringify({
            function_id: functionId,
            block_id: blockId,
            session_id: sessionId || '',
            user_message_id: userMessageId,
            user_message: userMessage.trim()
          }),
          signal: abortController.signal,
        });
      }

      if (!response.ok) {
        let serverMessage = '';
        try {
          const failure = await response.json();
          if (isEditLeaseLost(failure)) onEditLeaseLost?.();
          serverMessage = failure?.message || failure?.error || '';
        } catch (parseErr) { /* non-JSON body */ }
        throw new Error(serverMessage || `Server responded with ${response.status}`);
      }
      
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n\n');
        buffer = lines.pop() || '';

        for (const line of lines) {
          if (line.startsWith('data:')) {
            const jsonStr = line.substring(5).trim();
            const eventData = JSON.parse(jsonStr);

            if (eventData.type === 'compacting') {
                // Compaction can now run mid-turn, once the tool results so
                // far would push the next model call over the context bound,
                // so what has streamed stays on screen while the history is
                // summarised.
                setStreamingMessage(prev => prev ? {
                    ...prev,
                    streaming: true,
                    compacting: true,
                } : null);
                continue;
            }

            if (eventData.type === 'stream_start') {
                setStreamingMessage(prev => prev ? { ...prev, id: eventData.assistant_message_id, compacting: false } : null);
                continue;
            }

            if (eventData.type === 'error') {
                // A turn the backend ended early: the edit lease was taken
                // over mid-generation, or the turn failed. A failure before
                // the user message was persisted leaves nothing for the
                // message listener to replace the pending bubble with, so
                // clear it here; a persisted one arrives through the listener.
                if (isEditLeaseLost(eventData)) onEditLeaseLost?.();
                sawStreamError = true;
                setError(eventData.content || 'The assistant request failed.');
                setPendingUserMessage(null);
                continue;
            }

            if (eventData.type === 'done') {
                sawDone = true;
                // An error can follow a successfully saved implementation.
                // Revert only when the terminal acknowledgement says no code
                // was committed; the block listener adopts saved generations.
                if (eventData.function_changed) onImplementationCommitted();
                else restoreCommittedOutputs();
                continue;
            }

            if (eventData.type === 'content' && Array.isArray(eventData.data)) {
              setLivePreviewData(eventData.data);
              if (Array.isArray(eventData.manifest)) {
                setArtifactsManifest(eventData.manifest);
                // Adopt the implement call's generation (set by its
                // code_execution event, which the backend yields first) as soon
                // as its outputs render live. The post-stream block snapshot
                // carries the same generation, so the panel's remount key no
                // longer changes at commit — the chart would otherwise unmount
                // and refetch from GCS, blanking for the fetch round-trip.
                if (liveImplementGenerationRef.current) {
                  setManifestGeneration(liveImplementGenerationRef.current);
                }
              }
            }
            if (
              eventData.type === 'code_execution'
              && eventData.tool === 'implement_node_logic'
            ) {
              liveImplementGenerationRef.current = eventData.tool_use_id;
            }

            setStreamingMessage(prev => {
                if(!prev) return null;
                // Any model event means a compaction, if one ran, has finished.
                const base = { ...prev, compacting: false };
                const newContent = [...prev.content];

                // Thinking state (transient, not persisted)
                if (eventData.type === 'thinking_start') {
                    return { ...base, thinking: true, thinkingText: '' };
                } else if (eventData.type === 'thinking_delta') {
                    return { ...base, thinkingText: (prev.thinkingText || '') + eventData.thinking };
                } else if (eventData.type === 'thinking_complete') {
                    return { ...base, thinking: false, thinkingText: '' };
                }

                if (eventData.type === 'content') {
                    if (eventData.content) {
                      const lastPart = newContent.length > 0 ? newContent[newContent.length - 1] : null;
                      if (lastPart && lastPart.type === 'text') {
                          newContent[newContent.length - 1] = { ...lastPart, text: lastPart.text + eventData.content };
                      } else {
                          newContent.push({ type: 'text', text: eventData.content });
                      }
                    }
                } else if (eventData.type === 'tool_start') {
                    newContent.push({
                        type: 'tool_use',
                        id: eventData.tool_use_id,
                        tool_use_id: eventData.tool_use_id,
                        state: toolState(eventData.tool, eventData.type, `Starting ${eventData.tool}...`),
                        name: eventData.tool,
                        tool: eventData.tool
                    });
                } else if (eventData.type === 'code_execution' || eventData.type === 'code_error' || eventData.type === 'tool_complete') {
                    const toolIndex = newContent.findLastIndex(
                      (part) => part.type === 'tool_use' && part.tool_use_id === eventData.tool_use_id
                    );
                    if (toolIndex !== -1) {
                        const existingTool = newContent[toolIndex].tool || newContent[toolIndex].name;
                        const fallbackState = eventData.type === 'code_error'
                          ? 'Code execution failed'
                          : eventData.type === 'tool_complete'
                            ? eventData.status_message || 'Completed'
                            : 'Code executed successfully';
                        const eventTool = eventData.tool || existingTool;
                        newContent[toolIndex] = {
                          ...newContent[toolIndex],
                          name: eventTool,
                          tool: eventTool,
                          state: toolState(eventTool, eventData.type, fallbackState)
                        };
                    }
                }
                return { ...base, content: newContent };
            });

            if (eventData.type === 'data_available') {
              setLivePreviewData(eventData.data);
            }
          }
        }
      }
      if (!sawDone) {
        throw new Error('The connection ended before the assistant confirmed completion. Your unsaved message is still in the input.');
      }
      if (sawStreamError) {
        setStreamingMessage(null);
      } else {
        setStreamingMessage(prev => prev ? { ...prev, streaming: false } : null);
      }
    } catch (err) {
      restoreCommittedOutputs();
      if (err?.name === 'AbortError') {
        setError('Your editing session ended, so this request was stopped.');
      } else {
        setError(`Error: ${err.message || 'Could not process logic.'}`);
      }
      setPendingUserMessage(null); // Clear pending message on error
      setStreamingMessage(null);
    } finally {
      requestRef.current = null;
      if (registerAssistantAbort) registerAssistantAbort(null);
    }
  };

  const handleSave = async () => {
    if (!name.trim()) {
      setError('Please provide a name for the logic block.');
      return false;
    }
    // Route every Logic configuration/code write through the builder's draft
    // mutation callback so the head and block move together and the draft is
    // marked as ahead of the published version. There is no direct Firestore
    // fallback here. The Logic Assistant's generated code is committed by the
    // backend, which sets the same dirty flag.
    return onSave({ name: formatTextForDatabase(name.trim()) });
  };

  // The builder's Publish and Close ask this editor two things through its ref:
  // whether what is on screen differs from what was loaded, and to save it.
  // The baseline is the editable state as it stood when loading finished.
  const [loadedGeneration, setLoadedGeneration] = useState(0);
  const baselineRef = useRef('');
  const editSignature = name;
  useEffect(() => {
    baselineRef.current = editSignature;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadedGeneration]);
  useImperativeHandle(ref, () => ({
    isDirty: () => editSignature !== baselineRef.current,
    save: () => Promise.resolve(handleSave()),
  }));

  const allMessages = useMemo(() => {
    let combined = [...messages];

    if (pendingUserMessage) {
      const alreadySaved = messages.some((message) =>
        message.role === 'user' && message.id === pendingUserMessage.id
      );
      if (!alreadySaved) combined.push(pendingUserMessage);
    }

    if (streamingMessage) {
      const persistedIndex = combined.findIndex(
        (message) => message.role === 'assistant' && message.id === streamingMessage.id
      );
      if (persistedIndex >= 0) {
        combined[persistedIndex] = {
          ...combined[persistedIndex],
          content: streamingMessage.content,
          streaming: streamingMessage.streaming,
          thinking: streamingMessage.thinking,
          thinkingText: streamingMessage.thinkingText,
        };
      } else {
        combined.push(streamingMessage);
      }
    }

    return combined;
  }, [messages, pendingUserMessage, streamingMessage]);

  const hasSidePanel = Boolean(
    outputGcsPath || livePreviewData || artifactsManifest.length > 0 || lastCodeSnippet
    || logicSummary
  );

  // A capability chip adds its request to the draft on a new line; what the
  // user has already typed stays (M215).
  const injectCapabilityPrompt = (text) => {
    const current = draftMessage.trimEnd();
    onDraftMessageChange(current ? `${current}\n${text}` : text);
  };

  return (
    <Box sx={{ 
      p: 2, 
      // The chat column keeps its 600px; the outputs/code column is twice that,
      // hence 1800px and the 4/8 split below. maxWidth leaves the same 3.3% gap
      // on the right as `left` does on the left, so a dialog wider than the
      // window shrinks instead of running off the edge.
      width: hasSidePanel ? '1800px' : '800px',
      maxWidth: 'calc(100% - 6.6%)',
      height: '85vh',
      position:'absolute', top:100, left:'3.3%', zIndex:999,
      background:'#1A1E26', border:'1px solid rgba(255, 255, 255, 0.08)', 
      borderRadius:1, color: '#F0F2F5', boxShadow: '0 4px 12px rgba(0, 0, 0, 0.4)',
      borderTop: '3px solid #7E58B5', transition: 'width 0.3s ease',
      display: 'flex', flexDirection: 'column'
    }}>
      <Grid container spacing={2} sx={{ flexGrow: 1, overflow: 'hidden' }}>
        <Grid item xs={hasSidePanel ? 4 : 12} sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
          <Box sx={{ flexShrink: 0 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="h6" sx={{ color: '#7E58B5' }}>Configure Logic Block</Typography>
              <HelpButton 
                docPath="dashboard-page/data-functions/building-your-first-function/adding-logic-node" 
                tooltip="Logic Block Documentation"
              />
            </Box>
            {error && <Typography variant="body2" color="error" sx={{ mb: 2 }}>{error}</Typography>}
            <TextField fullWidth size="small" label="Name" value={name} onChange={(e) => setName(e.target.value)} sx={{ mb: 2 }}/>
            <Typography variant="body2" sx={{ mb: 2 }}>Chat with the AI to transform your data.</Typography>
          </Box>
          <Box sx={{ flexGrow: 1, overflowY: 'auto', mb:2, border:'1px solid rgba(255, 255, 255, 0.08)', p:1, borderRadius:1, backgroundColor: 'rgba(126, 88, 181, 0.05)' }}>
            {repairReason && <Alert severity="warning" sx={{ mb: 1 }}>{repairReason}</Alert>}
            <MessageDisplay messages={allMessages} color="#7E58B5" />
          </Box>
          <Box sx={{ flexShrink: 0 }}>
            <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ mb: 1 }}>
              <Chip
                size="small"
                label="Add a chart"
                clickable
                disabled={!!streamingMessage}
                onClick={() => injectCapabilityPrompt('Add a titled chart that clearly visualises the most useful pattern in this node output.')}
              />
              <Chip
                size="small"
                label="Excel export"
                clickable
                disabled={!!streamingMessage}
                onClick={() => injectCapabilityPrompt('Add an Excel export of this node output to the outputs directory.')}
              />
              <Chip
                size="small"
                label="Word report"
                clickable
                disabled={!!streamingMessage}
                onClick={() => injectCapabilityPrompt('Add a Word report summarising this node output to the outputs directory.')}
              />
            </Stack>
            <MessageInput
              onSendMessage={handleSendMessage}
              isDisabled={!!streamingMessage?.streaming}
              allowAttachments
              value={draftMessage}
              onChange={onDraftMessageChange}
              files={draftFiles}
              onFilesChange={onDraftFilesChange}
              clearOnSend={false}
            />
            <Divider sx={{ my: 2, borderColor: 'rgba(255, 255, 255, 0.08)' }} />
            <Stack direction="row" spacing={2}>
              <Button variant="contained" onClick={handleSave} sx={{ backgroundColor: '#7E58B5 !important' }}>Save</Button>
              <Button variant="text" onClick={onCancel} sx={{ color: '#ADB5BD' }}>Cancel</Button>
            </Stack>
          </Box>
        </Grid>
        {hasSidePanel && (
          <Grid item xs={8} sx={{ height: '100%' }}>
            <Box key={manifestGeneration || 'manifest'} sx={{ height: '100%' }}>
              <NodeOutputsPanel
                manifest={artifactsManifest}
                outputGcsPath={outputGcsPath}
                livePreviewData={livePreviewData}
                code={lastCodeSnippet}
                logicSummary={logicSummary}
                activeTab={activePanelTab}
                onTabChange={setActivePanelTab}
              />
            </Box>
          </Grid>
        )}
      </Grid>
    </Box>
  );
});

export default LogicBlockConfig;
